import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from 'react';

// Cloudflare Turnstile без npm-зависимости: скрипт грузится один раз,
// виджет рендерится явно (render=explicit), чтобы контролировать сброс.
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

let scriptPromise = null;

function loadScript() {
    if (scriptPromise) return scriptPromise;

    scriptPromise = new Promise((resolve, reject) => {
        if (window.turnstile) {
            resolve();
            return;
        }
        const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
        const script = existing || document.createElement('script');
        script.addEventListener('load', () => resolve());
        script.addEventListener('error', () => reject(new Error('Failed to load Turnstile')));
        if (!existing) {
            script.src = SCRIPT_SRC;
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
        }
    });

    return scriptPromise;
}

/**
 * Токен Turnstile одноразовый и живёт ~5 минут, поэтому после КАЖДОЙ
 * неудачной отправки формы виджет нужно сбросить через ref.reset(),
 * иначе повторная попытка уйдёт с уже потраченным токеном.
 *
 * <Turnstile ref={captchaRef} onVerify={setToken} />
 */
const Turnstile = forwardRef(({ onVerify, theme = 'auto', className = '' }, ref) => {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const onVerifyRef = useRef(onVerify);
    const [loadError, setLoadError] = useState(false);

    // Держим колбэк в ref, чтобы не перерисовывать виджет при каждом рендере родителя
    useEffect(() => { onVerifyRef.current = onVerify; }, [onVerify]);

    useImperativeHandle(ref, () => ({
        reset() {
            if (window.turnstile && widgetIdRef.current !== null) {
                window.turnstile.reset(widgetIdRef.current);
            }
            onVerifyRef.current?.('');
        },
    }), []);

    useEffect(() => {
        if (!SITE_KEY) return undefined;

        let cancelled = false;

        loadScript()
            .then(() => {
                if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;
                widgetIdRef.current = window.turnstile.render(containerRef.current, {
                    sitekey: SITE_KEY,
                    theme,
                    callback: (token) => onVerifyRef.current?.(token),
                    'expired-callback': () => onVerifyRef.current?.(''),
                    'error-callback': () => onVerifyRef.current?.(''),
                });
            })
            .catch(() => { if (!cancelled) setLoadError(true); });

        return () => {
            cancelled = true;
            if (window.turnstile && widgetIdRef.current !== null) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, [theme]);

    if (!SITE_KEY) {
        return (
            <p className="text-amber-500 text-sm my-3">
                Captcha is not configured (REACT_APP_TURNSTILE_SITE_KEY is missing).
            </p>
        );
    }

    if (loadError) {
        return (
            <p className="text-red-500 text-sm my-3">
                Could not load the captcha. Please disable blockers and reload the page.
            </p>
        );
    }

    return <div ref={containerRef} className={`my-4 ${className}`} />;
});

Turnstile.displayName = 'Turnstile';

export default Turnstile;
